import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styles from './ZakatCalculator.module.css';

const ZAKAT_RATE = 0.025;

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtBig(n) {
  if (!n && n !== 0) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9)  return '$' + (n / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6)  return '$' + (n / 1e6).toFixed(2) + 'M';
  return '$' + n.toLocaleString();
}

export default function ZakatCalculator({ financials, isETF = false }) {
  const f = financials;

  const [shares,      setShares]      = useState('');
  const [zakatDate,   setZakatDate]   = useState(todayStr());
  const [investorType, setInvestorType] = useState('long-term'); // 'day-trader' | 'long-term'
  const [precisePct,  setPrecisePct]  = useState('25');          // editable % for precise method
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // Price fetch state
  const [priceData,    setPriceData]    = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError,   setPriceError]   = useState('');

  const fetchPrice = useCallback(async () => {
    if (!zakatDate || !f?.ticker) return;
    setPriceLoading(true);
    setPriceError('');
    try {
      const { data } = await axios.get(`/api/price-on-date/${f.ticker}?date=${zakatDate}`);
      setPriceData(data);
    } catch (err) {
      setPriceError(err.response?.data?.error || 'Could not fetch price for that date.');
      setPriceData(null);
    } finally {
      setPriceLoading(false);
    }
  }, [zakatDate, f?.ticker]);

  useEffect(() => { fetchPrice(); }, [fetchPrice]);

  const sharesNum  = parseFloat(shares) || 0;
  const price      = priceData?.price || 0;
  const marketVal  = price * sharesNum;

  // CRI balance sheet values
  const cash        = (f?.cashAndEquivalents || 0) + (f?.shortTermInvestments || 0);
  const receivables = f?.netReceivables    || 0;
  const inventory   = f?.inventory         || 0;
  const sharesOut   = f?.sharesOutstanding || 0;
  const criPerShare = sharesOut > 0 ? (cash + receivables + inventory) / sharesOut : 0;
  const criExposure = criPerShare * sharesNum;
  const hasCriData  = sharesOut > 0 && (cash + receivables + inventory) > 0;

  // Precise % method uses user-editable percentage
  const precisePctNum = Math.min(100, Math.max(0, parseFloat(precisePct) || 0));

  const methods = [
    {
      key:         'simple',
      label:       investorType === 'day-trader' ? 'Full Market Value' : 'Simple & Safe',
      description: investorType === 'day-trader'
        ? 'Pay 2.5% on the full market value of your stocks. Standard approach for active traders.'
        : 'Pay 2.5% on the full market value of your stocks. The most conservative approach.',
      zakatable:   marketVal,
      zakat:       marketVal * ZAKAT_RATE,
      scholars:    investorType === 'day-trader'
        ? ['Ibn Qudamah — Al-Mughni (trade goods analogy)', 'AAOIFI Zakat Standard']
        : ['Ibn Baz (ra)', 'Ibn Uthaymeen (ra)'],
      recommended: investorType === 'day-trader',
      needsPrice:  true,
    },
    ...(!isETF && investorType === 'long-term' ? [
      {
        key:         '25pct',
        label:       '25% Rule',
        description: 'Only ~25% of a company\'s value is typically zakatable (cash, receivables). Best for most long-term investors.',
        zakatable:   marketVal * 0.25,
        zakat:       marketVal * 0.25 * ZAKAT_RATE,
        scholars:    [
          'Dr. Yusuf al-Qaradawi — Fiqh az-Zakah',
          'Dr. Wahbah al-Zuhayli — Fiqh al-Islami wa Adillatuhu',
          'AAOIFI asset-based zakat approach',
        ],
        recommended: true,
        needsPrice:  true,
      },
      {
        key:         'precise',
        label:       'Precise Asset %',
        description: 'Use the exact % from the company\'s factsheet. Best when you have the data.',
        zakatable:   marketVal * (precisePctNum / 100),
        zakat:       marketVal * (precisePctNum / 100) * ZAKAT_RATE,
        scholars:    ['AAOIFI Shariah Standard No. 35', 'International Islamic Fiqh Academy (OIC)'],
        recommended: false,
        needsPrice:  true,
        preciseInput: true,
      },
      {
        key:         'cri',
        label:       'CRI Method',
        description: hasCriData
          ? 'Cash + Receivables + Inventory from the latest annual balance sheet, divided by shares outstanding.'
          : 'Balance sheet data unavailable for this ticker.',
        zakatable:   hasCriData ? criExposure : null,
        zakat:       hasCriData ? criExposure * ZAKAT_RATE : null,
        scholars:    ['AAOIFI Shariah Standard No. 35', 'Mufti Taqi Usmani'],
        recommended: false,
        needsPrice:  false,
        criDetail:   hasCriData,
      },
    ] : []),
  ];

  const hasInput = sharesNum > 0;
  const showDay  = investorType === 'day-trader';

  return (
    <div className={styles.wrap}>

      {/* ── Investor type ── */}
      <div className={styles.stepBlock}>
        <p className={styles.stepQuestion}>Are you a trader or a long-term investor?</p>
        <p className={styles.stepSub}>This decides how your Zakat on stocks is calculated.</p>
        <div className={styles.typeGrid}>
          {[
            {
              key:   'day-trader',
              icon:  '↗',
              title: 'I buy & sell frequently',
              desc:  'You trade stocks regularly for profit. Zakat is straightforward — 2.5% of the total market value.',
            },
            {
              key:   'long-term',
              icon:  '◎',
              title: 'I invest for the long term',
              desc:  'You hold stocks for months or years. Scholars offer 3 valid methods — we\'ll show all of them so you can pick one.',
              badge: 'RECOMMENDED',
            },
          ].map(({ key, icon, title, desc, badge }) => (
            <button
              key={key}
              className={`${styles.typeCard} ${investorType === key ? styles.typeCardActive : ''}`}
              onClick={() => setInvestorType(key)}
            >
              <div className={styles.typeCardTop}>
                <span className={styles.typeIcon}>{icon}</span>
                <span className={styles.typeTitle}>{title}</span>
                {badge && <span className={styles.typeBadge}>{badge}</span>}
              </div>
              <p className={styles.typeDesc}>{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* ── Shares + date ── */}
      <div className={styles.stepBlock}>
        <div className={styles.inputRow}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Shares owned</label>
            <input
              className={styles.input}
              type="number"
              min="0"
              step="any"
              placeholder="e.g. 100"
              value={shares}
              onChange={e => setShares(e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Zakat year-end date</label>
            <input
              className={styles.input}
              type="date"
              max={todayStr()}
              value={zakatDate}
              onChange={e => setZakatDate(e.target.value)}
            />
          </div>
        </div>

        {/* Price fetch status */}
        <div className={styles.priceLine}>
          {priceLoading && <span className={styles.priceLoading}>Fetching price…</span>}
          {priceError && !priceLoading && <span className={styles.priceError}>{priceError}</span>}
          {priceData && !priceLoading && (
            <span className={styles.priceOk}>
              <strong>{fmtMoney(priceData.price)}</strong>
              {priceData.isApproximation
                ? ` · nearest trading day: ${priceData.actualDate}`
                : ` · closing price on ${priceData.actualDate}`}
            </span>
          )}
        </div>
      </div>

      {/* ── Methods ── */}
      {(hasInput || !isETF) && (
        <div className={styles.methodsBlock}>
          {!showDay && (
            <div className={styles.methodsHeader}>
              <p className={styles.methodsTitle}>
                {isETF ? 'Zakat estimate' : 'Pick a method to calculate your Zakat'}
              </p>
              {!isETF && (
                <p className={styles.methodsSub}>
                  All three are valid scholarly opinions. We show each amount so you can choose.
                </p>
              )}
            </div>
          )}

          {methods.map(m => {
            const zakatAmt = hasInput && price > 0 ? m.zakat : (m.key === 'cri' && hasInput && hasCriData ? m.zakat : null);
            const show = m.key === 'cri' ? (hasInput && hasCriData) : (hasInput && price > 0);

            return (
              <div key={m.key} className={`${styles.methodCard} ${m.recommended ? styles.methodCardRec : ''}`}>
                <div className={styles.methodCardTop}>
                  <div className={styles.methodCardLeft}>
                    <div className={styles.methodCardTitle}>
                      {m.label}
                      {m.recommended && <span className={styles.recBadge}>RECOMMENDED</span>}
                    </div>
                    <p className={styles.methodCardDesc}>{m.description}</p>
                    {m.preciseInput && (
                      <div className={styles.preciseRow}>
                        <span className={styles.preciseLabel}>Zakatable asset %</span>
                        <input
                          className={styles.preciseInput}
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={precisePct}
                          onChange={e => setPrecisePct(e.target.value)}
                        />
                        <span className={styles.preciseUnit}>%</span>
                        <span className={styles.preciseHint}>Not sure? Leave it at 25%.</span>
                      </div>
                    )}
                    {m.criDetail && (
                      <div className={styles.criDetail}>
                        <span>Cash {fmtBig(cash)}</span>
                        <span>Receivables {fmtBig(receivables)}</span>
                        <span>Inventory {fmtBig(inventory)}</span>
                        <span>÷ {(sharesOut / 1e6).toFixed(1)}M shares = {fmtMoney(criPerShare)}/share</span>
                      </div>
                    )}
                  </div>
                  <div className={styles.methodCardRight}>
                    <span className={styles.zakatAmt}>
                      {show ? fmtMoney(m.zakat) : '—'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {hasInput && !price && !priceLoading && methods.some(m => m.needsPrice) && (
            <p className={styles.noPriceNote}>Enter a valid date to see amounts.</p>
          )}
        </div>
      )}

      {/* ── Empty prompt ── */}
      {!hasInput && (
        <div className={styles.emptyPrompt}>
          Enter the number of shares you own to calculate your Zakat obligation.
        </div>
      )}

      {/* ── Scholarly sources ── */}
      <div className={styles.sourcesToggle}>
        <button className={styles.sourcesBtn} onClick={() => setSourcesOpen(o => !o)}>
          {sourcesOpen ? 'Hide' : 'Show'} scholarly sources
          <span className={styles.sourceCount}>{methods.flatMap(m => m.scholars).length}</span>
        </button>
      </div>

      {sourcesOpen && (
        <div className={styles.sourcesPanel}>
          <p className={styles.sourcesPanelTitle}>Stocks &amp; Shares</p>
          <div className={styles.sourcesGrid}>
            {methods.map(m => (
              <div key={m.key} className={styles.sourceGroup}>
                <p className={styles.sourceGroupLabel}>{m.label.toUpperCase()}</p>
                {m.scholars.map((s, i) => (
                  <p key={i} className={styles.sourceItem}>▸ {s}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className={styles.disclaimer}>
        All methods are based on recognized scholarly opinions. Pay Zakat according to the method you follow.
        This is an estimate — consult a qualified Islamic scholar for a binding ruling.
        Balance sheet data from the most recent annual filing ({f?.period || 'N/A'}).
      </p>
    </div>
  );
}
